import {
  ROLE,
  Currency,
  InvoiceStatus,
  PaymentStatus,
  SignupStatus,
  UploadStatus,
  AccountType,
} from "../shared/constant";
import globalUtils from "./globalUtils";

export const isClapAdmin = (userDetails: any) =>
  !!userDetails?.groups[0].includes(ROLE.ADMIN);

export const isClapManager = (userDetails: any) =>
  !!(userDetails?.groups[0] === ROLE.CLAP_MANAGER);

export const isAccounts = (userDetails: any) =>
  !!(userDetails?.groups[0] === ROLE.ACCOUNTS);

export const isManager = (userDetails: any) =>
  !!(userDetails?.groups[0] === ROLE.MANAGER);

export const isClapAdminORClapManager = (userDetails: any) =>
  !!(isClapAdmin(userDetails) || isClapManager(userDetails));

export const isAccountsORManager = (userDetails: any) =>
  !!(isAccounts(userDetails) || isManager(userDetails));

const clapUtils = {
  getUploadStatusColor: (status: string) => {
    switch (status) {
      case UploadStatus.QUEUED:
        return "bg-cyan";
      case UploadStatus.MAPPING:
        return "bg-warning";
      case UploadStatus.MAPPED:
        return "bg-lavender-indigo";
      case UploadStatus.PROCESSING:
        return "bg-warning";
      case UploadStatus.DELETING:
        return "bg-danger";
      case UploadStatus.PROCESSED:
        return "bg-american-green";
      case UploadStatus.FAILED:
        return "bg-danger";
      default:
        return "text-black";
    }
  },
  getAccountTypeColor: (type: string) => {
    switch (type) {
      case AccountType.LABEL:
        return "text-dodger-blue bg-bubbles border-pale-cyan";
      case AccountType.ARTIST:
        return "text-magenta bg-lavender-blush border-lavender-pink";
      case AccountType.LYRICIST:
      case AccountType.MEMBER:
        return "text-american-violet bg-magnolia border-mauve";
      case AccountType.COMPOSER:
        return "text-kelly-green bg-honeydew border-crayola";
      case AccountType.RIGHTHOLDER:
        return "text-beer bg-cosmic-latte border-caramel";
      case AccountType.CLAP:
        return "text-clap";

      default:
        return "";
    }
  },
  getInvoiceStatusColor: (status: string) => {
    switch (status) {
      case InvoiceStatus.DRAFT:
        return "bg-dodger-blue";
      case InvoiceStatus.VOID:
        return "bg-danger";
      case InvoiceStatus.INVOICE_SENT:
        return "bg-blue-violet";
      case InvoiceStatus.APPROVED:
        return "bg-orange-500";
      case InvoiceStatus.PAID:
        return "bg-american-green";

      default:
        return "";
    }
  },
  getPaymentStatusColor: (status: string) => {
    switch (status) {
      case PaymentStatus.NEW:
        return "bg-dodger-blue";
      case PaymentStatus.PAID:
        return "bg-american-green";

      default:
        return "";
    }
  },
  getSignupStatusColor: (status: string) => {
    switch (status) {
      case SignupStatus.NEW:
        return "bg-dodger-blue";
      case SignupStatus.PROCESSING:
        return "bg-warning";
      case SignupStatus.ACCEPTED:
        return "bg-american-green";
      case SignupStatus.REJECTED:
        return "bg-danger";

      default:
        return "";
    }
  },
  getCurrencyIcon: (currency: string) => {
    switch (currency) {
      case Currency.BDT:
        return "৳";
      case Currency.USD:
        return "$";
      case Currency.EUR:
        return "€";

      default:
        return "";
    }
  },
  getClientTypes: (types: any) => {
    return (
      <div className="flex flex-wrap gap-1">
        {types.map((type: any) => (
          <div
            key={type.id}
            className={`${clapUtils.getAccountTypeColor(
              type.label,
            )} rounded-sm border border-solid px-2`}
          >
            {type.label}
          </div>
        ))}
      </div>
    );
  },
  getPaymentType: (type: any) => {
    return (
      <div className="flex">
        <div
          className={`${clapUtils.getPaymentStatusColor(
            type,
          )} text-white rounded-sm px-2`}
        >
          {globalUtils.snakeCaseToCapitalize(type)}
        </div>
      </div>
    );
  },
  getSignupStatusType: (type: any) => {
    return (
      <div className="flex">
        <div
          className={`${clapUtils.getSignupStatusColor(
            type,
          )} text-white rounded-sm px-2`}
        >
          {globalUtils.snakeCaseToCapitalize(type)}
        </div>
      </div>
    );
  },
  getInvoiceType: (type: any) => {
    return (
      <div className="flex">
        <div
          className={`${clapUtils.getInvoiceStatusColor(
            type,
          )} text-white rounded-sm px-2`}
        >
          {globalUtils.snakeCaseToCapitalize(type)}
        </div>
      </div>
    );
  },
  getIncomeShare: (shares: any, clientTypes: any) => {
    // console.log(shares, clientTypes);
    let types = clientTypes.map((item: any) => item.label);
    if (types[0] === AccountType.MEMBER)
      types = [
        AccountType.LABEL,
        AccountType.ARTIST,
        AccountType.LYRICIST,
        AccountType.COMPOSER,
      ];
    return (
      <div className="flex flex-wrap gap-1">
        {Object.keys(shares).map(
          (item: any) =>
            types.includes(item) && (
              <div
                key={item}
                className={`${(clientTypes[0].label === AccountType.MEMBER &&
                    item === AccountType.LABEL &&
                    !shares[item]) ||
                  "bg-flash-white rounded-full px-2"
                  }`}
              >
                {
                  (() => {
                    if (clientTypes[0].label === AccountType.MEMBER) {
                      if (item === AccountType.LABEL && !shares[item])
                        return "";
                      return `${item === AccountType.LABEL ? "Producer" : item
                        } (${shares[item] || 0}%)`;
                    }
                    return `Producer (${shares[item] || 0}%)`;
                  })()

                  // clientTypes[0].label === AccountType.MEMBER
                  //   ? `${item} (${shares[item] || 0}%)`
                  //   : `${shares[item] || 0}%`
                }
              </div>
            ),
        )}
      </div>
    );
  },
  getNameFromFilter: (optionList: any, selectedOption: any, type: string) => {
    if (!selectedOption) return "";
    const filteredNames = optionList
      .filter((label: any) => selectedOption.includes(label.id))
      .map((label: any) => (type === "Track" ? label.title : label.name))
      ?.join(", ");

    return filteredNames;
  },
};

export default clapUtils;
